/**
 * Interlock NavRail — the shell-less navigation for every /interlock page.
 * Structure: Interlock logo mark, "Waiting on me" (the pinned inbox), a quiet
 * divider, then the engineering-pipeline group in work order (Components → System Diagram →
 * Requirements → Interface matrix → Risk Assessment → FMEA → Scoring →
 * Gates), and the user chip pinned to the bottom. "Functions" is not its own
 * rail entry — it's Stage 1 of the FMEA workflow (FmeaPage.tsx's FUNCTIONS
 * tab), reachable from FMEA.
 *
 * UX: the rail lists PLACES, not items — the old global TRADES list was
 * removed (a flat list of every trade is item-level noise that grows with the
 * project; "Waiting on me" surfaces trades needing you, and each interface
 * page lists its own trades).
 */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AvatarBadge } from './AvatarBadge';
import {
  Inbox,
  ShieldAlert,
  ListTree,
  Grid3x3,
  Workflow,
  DoorClosed,
  ScrollText,
  Users,
  Boxes,
  ClipboardCheck,
  Hammer,
  Zap,
  Menu,
  X,
  type LucideIcon,
} from 'lucide-react';
import { can } from '@fmeastudio/shared';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  confirmSpaNavigation,
  type InterlockNotification,
} from '../../api/interlockApi';
import {
  INTERLOCK_USERS,
  fetchInterlockUsers,
  getActor,
  getActorId,
  setActorId,
  subscribeActor,
  type InterlockUser,
} from '../../utils/interlockActor';
import { useAuth } from '../../contexts/AuthContext';
import { announce } from './liveRegion';
import { GlobalSearch } from './GlobalSearch';

/** Compact relative time: "just now", "4m", "2h", "3d". */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

const NOTIF_POLL_MS = 30_000;

/**
 * The nav-rail bell — unread count (30s poll), a dropdown list, and click-to-read
 * that navigates the deep link. Kept small: no dedicated notifications page.
 */
function NavBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState<InterlockNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  // A1 — tracks the last-seen unread count across polls so a rise (not the
  // first load, not a fall from mark-as-read) announces into the live region;
  // undefined until the first fetch resolves so mount doesn't announce.
  const prevUnread = useRef<number | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      const r = await fetchNotifications(20);
      setItems(r.data);
      setUnread(r.unreadCount);
      if (prevUnread.current !== undefined && r.unreadCount > prevUnread.current) {
        const delta = r.unreadCount - prevUnread.current;
        announce(`${delta} new notification${delta === 1 ? '' : 's'}`);
      }
      prevUnread.current = r.unreadCount;
    } catch {
      /* bell is best-effort; a failed poll just keeps the last state */
    }
  }, []);

  // Poll on mount + every 30s, and reload when the acting user switches.
  useEffect(() => {
    load();
    const t = setInterval(load, NOTIF_POLL_MS);
    const unsub = subscribeActor(() => load());
    return () => {
      clearInterval(t);
      unsub();
    };
  }, [load]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const onItemClick = async (n: InterlockNotification) => {
    setOpen(false);
    if (!n.isRead) {
      setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, isRead: true } : i)));
      setUnread((u) => Math.max(0, u - 1));
      try {
        await markNotificationRead(n.id);
      } catch {
        /* optimistic; next poll reconciles */
      }
    }
    if (n.actionUrl) navigate(n.actionUrl);
  };

  const onReadAll = async () => {
    setItems((prev) => prev.map((i) => ({ ...i, isRead: true })));
    setUnread(0);
    try {
      await markAllNotificationsRead();
    } catch {
      /* optimistic */
    }
  };

  return (
    <div className="ilk-bell" ref={wrapRef}>
      <button
        type="button"
        className="ilk-bell-btn"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        title="Unread notifications across the whole project (all activity) — not just items needing your action"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3a5 5 0 0 0-5 5c0 5-2 6-2 6h14s-2-1-2-6a5 5 0 0 0-5-5Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10 20a2 2 0 0 0 4 0"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
        {unread > 0 && <span className="ilk-bell-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className="ilk-bell-dropdown" role="menu">
          <div className="ilk-bell-head">
            <span className="ilk-bell-head-title">NOTIFICATIONS</span>
            {unread > 0 && (
              <button type="button" className="ilk-bell-readall" onClick={onReadAll}>
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="ilk-bell-empty">You're all caught up.</div>
          ) : (
            <div className="ilk-bell-list">
              {items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`ilk-bell-item${n.isRead ? '' : ' is-unread'}`}
                  role="menuitem"
                  onClick={() => onItemClick(n)}
                >
                  {!n.isRead && <span className="ilk-bell-dot" aria-hidden="true" />}
                  <span className="ilk-bell-item-body">
                    <span className="ilk-bell-item-title">{n.title}</span>
                    <span className="ilk-bell-item-msg">{n.message}</span>
                  </span>
                  <span className="ilk-bell-item-time">{relativeTime(n.createdAt)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A1 (UX Wave 1 slim-down) — the rail user chip's popover. Directive: "the pop
 * up is just settings/sign out etc, more clean — then the settings page can
 * expand a lot more." So this is now a plain MENU: identity header (avatar /
 * name / role chip), then "Settings" → /interlock/settings, "Admin settings"
 * (acting-role-gated) → /interlock/admin, and "Sign out" (real Tracer-SSO
 * logout via AuthContext — hidden in dev, see below). Notification prefs
 * (email/in-app + categories) moved to the Settings page; this popover no
 * longer fetches them at all. Dev-only ACT AS section stays (persona audits
 * key off it).
 *
 * Sign-out gating: in dev (`import.meta.env.DEV`) the backend runs without
 * JWT_SECRET, so auth.middleware.ts auto-authenticates every request as
 * `local-admin` regardless of cookie state (see that file's "MODE 1: no
 * JWT_SECRET = dev mode = local admin" comment) — POST /api/auth/logout would
 * clear a cookie that isn't gating anything and the very next `/api/auth/me`
 * would re-authenticate as local-admin anyway. That makes "Sign out"
 * meaningless in dev, so it's simply not rendered there. In prod, the entire
 * sign-out flow lives in ONE place: AuthContext's `logout()` (POST
 * /api/auth/logout to clear the cookie, then redirect to the Tracer SSO
 * login). This popover calls that implementation — the legacy shell's
 * Navigation.tsx sign-out button is likewise just a caller of it; neither
 * surface carries flow logic of its own.
 */
function ProfilePopover({
  isDev,
  actor,
  actorId,
  switcherUsers,
  open,
  onClose,
  onSwitchActor,
  onOpenAdmin,
  onOpenSettings,
  onSignOut,
}: {
  isDev: boolean;
  actor: InterlockUser;
  actorId: string;
  switcherUsers: InterlockUser[];
  open: boolean;
  onClose: () => void;
  onSwitchActor: (id: string) => void;
  onOpenAdmin: () => void;
  onOpenSettings: () => void;
  onSignOut?: () => void;
}) {
  if (!open) return null;

  return (
    <div className="ilk-actor-menu ilk-profile-menu" role="menu">
      <div className="ilk-profile-section">
        <div className="ilk-profile-row">
          <AvatarBadge user={actor} className="ilk-avatar ilk-avatar-sm ilk-avatar-party" />
          <span className="ilk-profile-idcol">
            <span className="ilk-profile-name">{actor.name}</span>
            {actor.email && <span className="ilk-profile-email">{actor.email}</span>}
          </span>
          <span className={`ilk-actor-opt-role role-${actor.role.toLowerCase()}`}>
            {actor.role}
          </span>
        </div>
        {actor.discipline && <div className="ilk-profile-discipline">{actor.discipline}</div>}
      </div>

      <div className="ilk-profile-section">
        <button
          type="button"
          role="menuitem"
          className="ilk-profile-menuitem"
          onClick={() => {
            onClose();
            onOpenSettings();
          }}
        >
          Settings
        </button>
        {actor.role === 'ADMIN' && (
          <button
            type="button"
            role="menuitem"
            className="ilk-profile-menuitem"
            onClick={() => {
              onClose();
              onOpenAdmin();
            }}
          >
            Admin settings
          </button>
        )}
        {!isDev && onSignOut && (
          <button
            type="button"
            role="menuitem"
            className="ilk-profile-menuitem ilk-profile-menuitem-danger"
            onClick={() => {
              onClose();
              onSignOut();
            }}
          >
            Sign out
          </button>
        )}
      </div>

      {isDev && (
        <div className="ilk-profile-section">
          <div className="ilk-actor-menu-cap">ACT AS · dev</div>
          {switcherUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              role="menuitemradio"
              aria-checked={u.id === actorId}
              className={`ilk-actor-opt${u.id === actorId ? ' is-active' : ''}`}
              title={u.email ?? undefined}
              onClick={() => onSwitchActor(u.id)}
            >
              <AvatarBadge user={u} className="ilk-avatar ilk-avatar-xs ilk-avatar-party" />
              <span className="ilk-actor-opt-main">
                {/* Y5d — name + role chip + email on their own line so two users
                    sharing a first name (e.g. two Nicks) are distinguishable at a
                    glance, not just by hovering for a tooltip. */}
                <span className="ilk-actor-opt-namerow">
                  <span className="ilk-actor-opt-name">{u.short}</span>
                  <span className={`ilk-actor-opt-role role-${u.role.toLowerCase()}`}>
                    {u.role}
                  </span>
                </span>
                {u.email && <span className="ilk-actor-opt-email">{u.email}</span>}
              </span>
              {u.id === actorId && <span className="ilk-actor-opt-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export type NavKey =
  | 'home'
  | 'system'
  | 'requirements'
  | 'matrix'
  | 'components'
  | 'risks'
  | 'fmea'
  | 'gates'
  | 'build'
  | 'commissioning'
  | 'acceptance'
  // Keys kept so the pages' own <InterlockScaffold nav=…> props still type-check.
  // These surfaces were dropped from the RAIL on 2026-07-28 (owner call) but
  // their ROUTES still resolve, so existing links and bookmarks are unaffected.
  | 'campaigns'
  | 'status'
  | 'workload'
  | 'audit'
  | 'stress';

// A1 (UX Wave A1) — every rail link gets a lucide glyph so the rail scans by
// shape, not just by reading nine lines of text top to bottom.
//
// B1 (nav pipeline, owner-locked 2026-07-15) — the rail is now shaped as:
// "Waiting on me" pinned FIRST (the inbox, NOT a pipeline stage; a quiet
// divider separates it — see the render below), then the engineering pipeline
// in work order. REMOVED: "Interface register" (absorbed into the matrix
// stage — parcel B4: the list is the matrix page's ?view=list projection and
// /interlock/interfaces redirects there, params intact) and the disabled
// "Since you last looked" stub. Workshop was dropped earlier (UX Wave 1 +
// FMEA F4 — /interlock/workshop redirects to /interlock/fmea).
const NAV_LINKS: {
  key: NavKey;
  label: string;
  to?: string;
  disabled?: boolean;
  Icon: LucideIcon;
}[] = [
  { key: 'home', label: 'Waiting on me', to: '/interlock', Icon: Inbox },
  // Components — pinned to the top of the pipeline (right after the personal
  // inbox) per owner direction. Route: /interlock/components-list →
  // per-component /interlock/components/:id/spec.
  { key: 'components', label: 'Components', to: '/interlock/components-list', Icon: Boxes },
  { key: 'system', label: 'System Diagram', to: '/interlock/system', Icon: Workflow },
  // B1 — Requirements is pipeline stage 2.
  { key: 'requirements', label: 'Requirements', to: '/interlock/requirements', Icon: ScrollText },
  // Nav B3 (2026-07-15) added a standalone "Functions" rail item pointing at
  // /interlock/functions; owner call on 2026-07-21 removed it — Stage 1 of
  // the FMEA workflow IS function definition (FmeaPage.tsx's FUNCTIONS tab,
  // hosting the same rich workshop this rail item used to open), so a
  // separate top-level nav entry duplicated it. /interlock/functions now
  // redirects into that tab (App.tsx) so old links/bookmarks still resolve.
  { key: 'matrix', label: 'Interface matrix', to: '/interlock/matrix', Icon: Grid3x3 },
  // UX Wave C2 — renamed "Risk register" → "Risk Assessment" (now editable;
  // Excel-familiar L×S/residual/mitigation matrix). Route + icon unchanged so
  // every existing deep link into `/interlock/risks/:ref` still resolves.
  { key: 'risks', label: 'Risk Assessment', to: '/interlock/risks', Icon: ShieldAlert },
  // UX Wave C2 — top-level FMEA page (was hidden inside Workshop before that
  // wave). ListTree reads as "many failure modes branching from one program",
  // distinct from ShieldAlert (risk).
  { key: 'fmea', label: 'FMEA', to: '/interlock/fmea', Icon: ListTree },
  { key: 'gates', label: 'Gates', to: '/interlock/gates', Icon: DoorClosed },
  // BUILD PHASE (2026-07-28) — the execution surfaces. Build and Commissioning are
  // separate rail items, not tabs on one: they have different RESPONSIBILITY OWNERS
  // (Assembly and Construction vs Commissioning) and the Completion and Commissioning
  // Framework (302 006 110) puts a formal transfer between them. One page would hide
  // the boundary those documents exist to enforce.
  { key: 'build', label: 'Build', to: '/interlock/build', Icon: Hammer },
  { key: 'commissioning', label: 'Commissioning', to: '/interlock/commissioning', Icon: Zap },
  { key: 'acceptance', label: 'FAT / SAT', to: '/interlock/acceptance', Icon: ClipboardCheck },
];

// Scoring, Readiness, Workload, Audit and Stress cases were removed from the rail
// entirely on 2026-07-28 (owner call: "Readiness is just Gates really"), so the old
// dev-visible / prod-hidden split has nothing left to hide. Their ROUTES are
// deliberately untouched — /interlock/campaigns, /interlock/status,
// /interlock/workload, /interlock/audit and /interlock/stress all still resolve, so
// bookmarks and cross-page links keep working. Only the rail links are gone.
const VISIBLE_NAV_LINKS = NAV_LINKS;

export function NavRail({ active }: { active?: NavKey }) {
  const navigate = useNavigate();
  const [actorId, setActor] = useState(getActorId());
  const [profileOpen, setProfileOpen] = useState(false);
  // R4 (persona-audit Loop 3, MAJOR) — below the ~900px breakpoint the rail
  // collapses from a fixed left sidebar into a top bar + drawer (see the
  // `@media (max-width: 900px)` block in interlock.css). `menuOpen` gates the
  // drawer's visibility; the hamburger toggle button that flips it only
  // renders (via CSS) at those narrow widths, so it's inert/hidden on desktop.
  const [menuOpen, setMenuOpen] = useState(false);
  // X9b — dev switcher lists the real active-user roster (GET /api/auth/users),
  // not just the three hardcoded seed engineers; falls back to INTERLOCK_USERS on
  // fetch failure (see fetchInterlockUsers in utils/interlockActor.ts).
  const [switcherUsers, setSwitcherUsers] = useState<InterlockUser[]>(INTERLOCK_USERS);
  const isDev = import.meta.env.DEV;
  const actor = getActor();
  // Sign-out: the flow itself is implemented ONLY in AuthContext's logout()
  // (POST /api/auth/logout + Tracer-SSO redirect); this rail — like the legacy
  // shell's Navigation.tsx button — is just a caller. The popover hides the
  // item entirely in dev — see ProfilePopover's doc comment.
  const { logout } = useAuth();

  useEffect(() => {
    if (!isDev) return;
    let live = true;
    fetchInterlockUsers().then((users) => {
      if (live) setSwitcherUsers(users);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line -- fetch is idempotent/memoized; run once on mount
  }, []);

  // subscribeActor fires with the actor *id*; on a same-user change (e.g. the
  // profile photo being uploaded/removed via setActorAvatar) the id is
  // unchanged, so `setActor(id)` is a no-op React bails on and the rail badge
  // would keep showing the stale avatar until the next navigation/reload. Bump
  // a tick alongside so getActor() (line ~433) is re-read on every notification,
  // not only when the identity itself switches.
  const [, bumpTick] = useState(0);
  useEffect(
    () =>
      subscribeActor((id) => {
        setActor(id);
        bumpTick((n) => n + 1);
      }),
    []
  );

  // Close the drawer if the viewport widens past the collapse breakpoint
  // (e.g. rotating a tablet, or a devtools resize mid-session) so it never
  // gets stuck open once the sidebar layout has room to render again.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 901px)');
    const onChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) setMenuOpen(false);
    };
    onChange(mq);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Close the profile popover on outside click (same pattern as NavBell above).
  const profileWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!profileOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (profileWrapRef.current && !profileWrapRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [profileOpen]);

  // Y6 (round 4) — SPA-nav unsaved guard: a rail link click is a React Router
  // `navigate()`, which the CampaignDetailPage's beforeunload/popstate guards
  // never see. Route every rail navigation through the shared dirty registry
  // (interlockApi.ts) first.
  const guardedNavigate = useCallback(
    (to: string) => {
      if (!confirmSpaNavigation()) return;
      // Any rail navigation should collapse the mobile drawer — otherwise the
      // menu stays pinned open over the newly-routed page.
      setMenuOpen(false);
      navigate(to);
    },
    [navigate]
  );

  return (
    <nav className={`ilk-rail${menuOpen ? ' is-menu-open' : ''}`} aria-label="Interlock navigation">
      <div className="ilk-rail-topbar">
        <div className="ilk-rail-brand">
          <div className="ilk-rail-mark" />
          <span className="ilk-rail-name">Interlock</span>
          <NavBell />
        </div>
        {/* R4 — hamburger toggle for the collapsed (<=900px) drawer; CSS hides
            this entirely at desktop widths so it never intercepts a click
            there. Sits in the always-visible top bar alongside the brand and
            bell so it's reachable regardless of scroll position. */}
        <button
          type="button"
          className="ilk-rail-menu-toggle"
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {menuOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
        </button>
      </div>

      {/* R4 — everything below the top bar (strapline, search, pipeline links,
          profile footer) is one drawer group. On desktop it's laid out as the
          usual stacked sidebar; below 900px it's hidden by default and shown
          as a single overlay panel anchored under the top bar when
          `is-menu-open` is set, instead of wrapping into loose flex items
          that used to scatter across the page above the content card. */}
      <div className="ilk-rail-menu">
        {/* R3 W3 (X10 — Newcomer orientation): the nav rail is on every page, so this
            is the one place a one-line "what is this app for" strapline reaches
            someone regardless of which page they landed on cold. */}
        <div className="ilk-rail-strapline">engineering agreements, on the record</div>

        {/* Global search — always reachable jump-to-entity box (project-scoped,
            server-side). Sits directly under the brand so it's the first thing a
            returning engineer reaches on any page. */}
        <div className="ilk-rail-search">
          <GlobalSearch />
        </div>

        <div className="ilk-rail-divider" />
        <div className="ilk-rail-group">
          {VISIBLE_NAV_LINKS
            // W5 — the Workload view is a per-person load aggregate, so the rail
            // entry only shows for management (ARBITRATE = Manager / PM / Admin),
            // matching the backend endpoint's gate. Reads the CURRENT actor's
            // role so a dev "act as" switch to an engineer hides it live.
            .filter((l) => l.key !== 'workload' || can(actor.role, 'ARBITRATE'))
            .map((l, i) => (
              <Fragment key={l.key}>
                {/* B1 — quiet divider between the pinned inbox ("Waiting on me")
                  and the engineering-pipeline group below it. */}
                {i === 1 && <div className="ilk-rail-divider" aria-hidden="true" />}
                <button
                  type="button"
                  className={`ilk-rail-item ilk-rail-item-icon${l.key === active ? ' is-active' : ''}`}
                  disabled={l.disabled}
                  title={l.disabled ? 'Arrives with the Interlock wedge' : undefined}
                  onClick={l.to ? () => guardedNavigate(l.to as string) : undefined}
                >
                  <l.Icon className="ilk-rail-item-glyph" size={14} aria-hidden="true" />
                  <span>{l.label}</span>
                </button>
              </Fragment>
            ))}
        </div>

        <div className="ilk-rail-userwrap" ref={profileWrapRef}>
          <ProfilePopover
            isDev={isDev}
            actor={actor}
            actorId={actorId}
            switcherUsers={switcherUsers}
            open={profileOpen}
            onClose={() => setProfileOpen(false)}
            onSwitchActor={(id) => {
              setActorId(id);
              setProfileOpen(false);
            }}
            onOpenAdmin={() => guardedNavigate('/interlock/admin')}
            onOpenSettings={() => guardedNavigate('/interlock/settings')}
            onSignOut={() => void logout()}
          />
          <button
            type="button"
            className="ilk-rail-user is-switch"
            title={
              isDev
                ? `Profile / act as (dev)${actor.email ? ` — ${actor.email}` : ''}`
                : `Profile${actor.email ? ` — ${actor.email}` : ''}`
            }
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen((v) => !v)}
          >
            <AvatarBadge user={actor} className="ilk-avatar ilk-avatar-sm ilk-avatar-party" />
            <span className="ilk-rail-user-name">{actor.name}</span>
            {actor.role === 'VIEWER' && (
              <span className="ilk-actor-opt-role role-viewer ilk-rail-viewer-pill">
                VIEWER · read-only
              </span>
            )}
            <span className="ilk-rail-user-caret">{profileOpen ? '▾' : '▸'}</span>
          </button>
        </div>
      </div>
    </nav>
  );
}
